import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createOrder } from "../../../api/orderService.js";
import api from "../../../api/api.js";

import {
    PageHeader,
    Panel,
    InfoRow,
    DarkButton,
} from "../../../components/ui/AdminUI";

import {
    NumberInput,
    TextInput,
    SelectBox,
    PrimaryButton,
} from "../../../components/ui/FormControls";

export default function ClientOrderCreatePage() {
    const navigate = useNavigate();

    const [form, setForm] = useState({
        totalAmount: "",
        currency: "EUR",
        externalOrderNumber: "",
        source: "Web",
        originCountryId: "",
        originCityId: "",
        originPostalCodeId: "",
        destinationCountryId: "",
        destinationCityId: "",
        destinationPostalCodeId: "",
    });

    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [postalCodes, setPostalCodes] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        loadLocations();
    }, []);

    const loadLocations = async () => {
        try {
            const [countriesRes, citiesRes, postalCodesRes] = await Promise.all([
                api.get("/country"),
                api.get("/city"),
                api.get("/postalcode"),
            ]);

            setCountries(Array.isArray(countriesRes.data) ? countriesRes.data : []);
            setCities(Array.isArray(citiesRes.data) ? citiesRes.data : []);
            setPostalCodes(Array.isArray(postalCodesRes.data) ? postalCodesRes.data : []);
        } catch (err) {
            console.error(err);
            setError("Failed to load location data.");
        }
    };

    const handleChange = (e) => {
        setForm((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const validateRoute = () =>
        form.originCountryId &&
        form.originCityId &&
        form.originPostalCodeId &&
        form.destinationCountryId &&
        form.destinationCityId &&
        form.destinationPostalCodeId;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!form.totalAmount || Number(form.totalAmount) <= 0) {
            setError("Total amount must be greater than 0.");
            return;
        }

        if (!validateRoute()) {
            setError("Please select origin and destination route.");
            return;
        }

        try {
            setLoading(true);

            const payload = {
                totalAmount: Number(form.totalAmount),
                currency: form.currency || "EUR",
                externalOrderNumber: form.externalOrderNumber || null,
                source: form.source || "Web",
                originCountryId: Number(form.originCountryId),
                originCityId: Number(form.originCityId),
                originPostalCodeId: Number(form.originPostalCodeId),
                destinationCountryId: Number(form.destinationCountryId),
                destinationCityId: Number(form.destinationCityId),
                destinationPostalCodeId: Number(form.destinationPostalCodeId),
            };

            const created = await createOrder(payload);

            if (created?.shipmentId) {
                navigate(`/payments/create/${created.shipmentId}`);
            } else {
                navigate("/client/orders");
            }
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to create order."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Create Order"
                subtitle="Create a new logistics order and define origin and destination route."
                action={
                    <DarkButton onClick={() => navigate("/client/orders")}>
                        Back to Orders
                    </DarkButton>
                }
            />

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <Panel title="Order Information" subtitle="Basic order and payment data.">
                    <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                        <NumberInput
                            label="Total Amount"
                            name="totalAmount"
                            step="0.01"
                            placeholder="Total amount"
                            value={form.totalAmount}
                            onChange={handleChange}
                            required
                        />

                        <TextInput
                            label="Currency"
                            name="currency"
                            placeholder="Currency"
                            value={form.currency}
                            onChange={handleChange}
                            required
                        />

                        <TextInput
                            label="External Order Number"
                            name="externalOrderNumber"
                            placeholder="External order number"
                            value={form.externalOrderNumber}
                            onChange={handleChange}
                        />

                        <TextInput
                            label="Source"
                            name="source"
                            placeholder="Source"
                            value={form.source}
                            onChange={handleChange}
                        />
                    </div>
                </Panel>

                <div className="grid xl:grid-cols-2 gap-6">
                    <Panel title="Origin" subtitle="Select where the shipment starts.">
                        <div className="space-y-4">
                            <SelectBox
                                label="Origin Country"
                                name="originCountryId"
                                value={form.originCountryId}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select origin country</option>
                                {countries.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name || c.countryName}
                                    </option>
                                ))}
                            </SelectBox>

                            <SelectBox
                                label="Origin City"
                                name="originCityId"
                                value={form.originCityId}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select origin city</option>
                                {cities.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name || c.cityName}
                                    </option>
                                ))}
                            </SelectBox>

                            <SelectBox
                                label="Origin Postal Code"
                                name="originPostalCodeId"
                                value={form.originPostalCodeId}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select origin postal code</option>
                                {postalCodes.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.code || p.postalCode || p.value}
                                    </option>
                                ))}
                            </SelectBox>
                        </div>
                    </Panel>

                    <Panel title="Destination" subtitle="Select where the shipment should arrive.">
                        <div className="space-y-4">
                            <SelectBox
                                label="Destination Country"
                                name="destinationCountryId"
                                value={form.destinationCountryId}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select destination country</option>
                                {countries.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name || c.countryName}
                                    </option>
                                ))}
                            </SelectBox>

                            <SelectBox
                                label="Destination City"
                                name="destinationCityId"
                                value={form.destinationCityId}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select destination city</option>
                                {cities.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name || c.cityName}
                                    </option>
                                ))}
                            </SelectBox>

                            <SelectBox
                                label="Destination Postal Code"
                                name="destinationPostalCodeId"
                                value={form.destinationPostalCodeId}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select destination postal code</option>
                                {postalCodes.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.code || p.postalCode || p.value}
                                    </option>
                                ))}
                            </SelectBox>
                        </div>
                    </Panel>
                </div>

                <Panel title="Route Summary">
                    <div className="grid md:grid-cols-3 gap-4 mb-5">
                        <InfoRow label="Amount" value={`${form.totalAmount || "—"} ${form.currency || ""}`} />
                        <InfoRow label="Source" value={form.source || "—"} />
                        <InfoRow label="External Order" value={form.externalOrderNumber || "—"} />
                    </div>

                    <PrimaryButton type="submit" disabled={loading}>
                        {loading ? "Creating..." : "Create Order"}
                    </PrimaryButton>
                </Panel>
            </form>
        </div>
    );
}